import {useRef, useState} from 'react';
import type {RouteComponentProps} from 'react-router-dom';

import {useDebounce} from 'shells/utils/hooks/use-debounce';

import {ThreadType} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {useGetSharedInboxConfig} from '../email/graphql/hooks/use-get-shared-inbox-config';
import {useGetLatestThreads, useGetChannelActiveStatuses} from '../graphql/hooks';
import {getThreadStatusFromSelectedTab} from '../helpers';
import {InboxPageContent} from '../shared-components';

import {SharedEmailInboxFeatureGatePage} from './shared-email-inbox-feature-gate-page';

interface Props extends RouteComponentProps<{page?: string; id?: string}> {
    assigneeIds: string[];
}

const THREAD_TYPE = ThreadType.EMAIL;

export function SharedEmailInboxPage(props: Props) {
    const selectedPage = props.match.params.page;
    const status = getThreadStatusFromSelectedTab(selectedPage);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 250);

    const initialThreadId = useRef(props.match.params.id).current;

    const {
        latestThreads,
        totalCount,
        isLoading: isLoadingLatestThreads,
        fetchMoreRows: fetchMoreThreads,
        hasNextPage: hasMoreThreads,
        refetch: refetchThreads,
    } = useGetLatestThreads({
        status,
        assignees: props.assigneeIds,
        threadType: THREAD_TYPE,
        findThreadId: initialThreadId || undefined,
        search: debouncedSearch || undefined,
    });

    const {channelActiveStatuses} = useGetChannelActiveStatuses({
        channelIds: latestThreads.map((thread) => thread.externalChannel.id),
    });

    const {sharedInboxConfig, isLoading: isLoadingSharedInboxConfig} = useGetSharedInboxConfig();

    if (!NutClientConfig.hasSharedEmailInbox) {
        return <SharedEmailInboxFeatureGatePage />;
    }

    const hasSharedAddresses = (sharedInboxConfig?.sharedAddresses?.length ?? 0) > 0;
    if (!hasSharedAddresses && !isLoadingSharedInboxConfig) {
        appHistory.push('/inbox/settings/email');
        return null;
    }

    if (channelActiveStatuses) {
        latestThreads.forEach((thread, index) => {
            // @ts-expect-error
            thread.isActive = channelActiveStatuses[index];
        });
    }

    return (
        <InboxPageContent
            {...props}
            selectedView={selectedPage}
            latestThreads={latestThreads}
            latestThreadsTotalCount={totalCount}
            isLoadingLatestThreads={isLoadingLatestThreads}
            fetchMoreThreads={fetchMoreThreads}
            hasMoreThreads={hasMoreThreads}
            assigneeIds={props.assigneeIds}
            threadType={THREAD_TYPE}
            search={search}
            onSearchChange={setSearch}
            refetchThreads={refetchThreads}
        />
    );
}
